import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatIcon from '@mui/icons-material/Chat';

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const heroAnim = {
    animation: 'allureFadeUp 0.85s ease-out both',
  } as const;

  return (
    <Box sx={{ backgroundColor: 'background.default', overflow: 'hidden' }}>
      {/* Hero éditorial */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '78vh', md: '90vh' },
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          color: 'common.white',
          backgroundImage: 'url("/images/welcome-dress.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'center top', md: 'center 20%' },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(180deg, ${alpha('#1c1b1a', 0.15)} 0%, ${alpha('#1c1b1a', 0.55)} 55%, ${alpha('#1c1b1a', 0.72)} 100%)`,
          },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
            pb: { xs: 8, md: 12 },
            pt: { xs: 18, md: 22 },
            ...heroAnim,
          }}
        >
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-end">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: '0.42em',
                  color: alpha('#fff', 0.82),
                  display: 'block',
                  mb: 2,
                  fontSize: '0.68rem',
                }}
              >
                Location de robes de soirée
              </Typography>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontWeight: 500,
                  mb: 2.5,
                  fontSize: isMobile ? '2.5rem' : '4.25rem',
                  lineHeight: 1.02,
                  color: '#FDFCFA',
                  textShadow: `0 2px 40px ${alpha('#000', 0.25)}`,
                }}
              >
                L&apos;élégance
                <br />
                <Box component="span" sx={{ fontStyle: 'italic', color: alpha('#E8DFD4', 0.98) }}>
                  se partage
                </Box>
              </Typography>
              <Typography
                sx={{
                  mb: { xs: 4, md: 5 },
                  fontSize: isMobile ? '1rem' : '1.125rem',
                  fontWeight: 400,
                  fontFamily: '"Outfit", sans-serif',
                  color: alpha('#fff', 0.88),
                  maxWidth: 440,
                  lineHeight: 1.75,
                  letterSpacing: '0.01em',
                }}
              >
                Une garde-robe confidentielle entre particulières. Esprit Paris, standards d&apos;une
                maison de luxe.
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Button
                  variant="contained"
                  disableElevation
                  size="large"
                  endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                  onClick={() => navigate('/search')}
                  sx={{
                    px: 3.5,
                    py: 1.75,
                    minWidth: { xs: '100%', sm: 220 },
                    backgroundColor: theme.palette.primary.main,
                    color: '#FAF8F5',
                    border: `1px solid ${alpha('#FAF8F5', 0.25)}`,
                    fontSize: '0.8rem',
                    letterSpacing: '0.14em',
                    '& .MuiButton-endIcon': { color: '#FAF8F5' },
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      color: '#FAF8F5',
                    },
                  }}
                >
                  Louer une robe
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                  onClick={() => navigate('/add-dress')}
                  sx={{
                    px: 3.5,
                    py: 1.75,
                    minWidth: { xs: '100%', sm: 220 },
                    fontSize: '0.8rem',
                    letterSpacing: '0.14em',
                    borderWidth: 2,
                    backgroundColor: alpha('#ffffff', 0.94),
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    borderColor: `${alpha(theme.palette.primary.main, 0.65)}`,
                    color: theme.palette.primary.main,
                    WebkitTextFillColor: theme.palette.primary.main,
                    backgroundImage: 'none',
                    '& .MuiButton-endIcon': {
                      color: 'currentColor',
                      opacity: 1,
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      backgroundImage: 'none',
                      borderColor: theme.palette.primary.main,
                      color: '#ffffff',
                      WebkitTextFillColor: '#ffffff',
                      '& .MuiButton-endIcon': { color: '#ffffff' },
                    },
                  }}
                >
                  Déposer ma robe
                </Button>
              </Stack>
              <Button
                variant="text"
                onClick={() => navigate('/register')}
                sx={{
                  mt: 2,
                  color: alpha('#fff', 0.75),
                  fontSize: '0.75rem',
                  letterSpacing: '0.18em',
                  textDecoration: 'underline',
                  textUnderlineOffset: 5,
                  '&:hover': { color: '#fff', backgroundColor: 'transparent' },
                }}
              >
                Créer un compte
              </Button>
            </Grid>
            {isMdUp && (
              <Grid item md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Box
                  sx={{
                    border: `1px solid ${alpha('#fff', 0.25)}`,
                    backgroundColor: alpha('#fff', 0.06),
                    backdropFilter: 'blur(8px)',
                    p: 4,
                    animation: 'allureFadeIn 1s ease-out 0.2s both',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: alpha('#fff', 0.75), letterSpacing: '0.3em', display: 'block', mb: 2 }}
                  >
                    Nouveau
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontWeight: 500,
                      color: '#FDFCFA',
                      lineHeight: 1.35,
                      mb: 2,
                    }}
                  >
                    Des pièces d&apos;exception, proches de vous.
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha('#fff', 0.78), lineHeight: 1.7 }}>
                    Essayage, échanges sécurisés, paiements protégés, comme sur les plateformes
                    internationales que vous aimez.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Bloc blanc respiration */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 8, md: 14 } }}>
        <Container maxWidth="md">
          <Typography
            align="center"
            variant="overline"
            sx={{ letterSpacing: '0.35em', color: 'text.secondary', display: 'block', mb: 3 }}
          >
            Notre promesse
          </Typography>
          <Typography
            align="center"
            variant="h3"
            sx={{
              fontFamily: '"Cormorant Garamond", serif',
              fontWeight: 500,
              mb: 4,
              fontSize: { xs: '1.85rem', md: '2.5rem' },
              lineHeight: 1.25,
            }}
          >
            L&apos;expérience d&apos;une boutique,
            <br />
            la liberté du partage.
          </Typography>
          <Typography
            align="center"
            color="text.secondary"
            sx={{
              maxWidth: 540,
              mx: 'auto',
              lineHeight: 1.85,
              fontSize: '1.02rem',
            }}
          >
            Beaucoup d&apos;espace, peu de bruit visuel : chaque robe est mise en valeur comme dans un
            lookbook. Parcourez, réservez, récupérez sans friction.
          </Typography>
        </Container>
      </Box>

      {/* Visuel éditorial large */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 360, md: 520 },
          backgroundImage: 'url("/images/welcome-dress.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(90deg, ${alpha('#FDFCFA', 0.92)} 0%, ${alpha('#FDFCFA', 0.45)} 45%, transparent 100%)`,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 12 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ letterSpacing: '0.28em', color: 'primary.main', mb: 2 }}>
                Sélection
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, mb: 3, lineHeight: 1.2 }}
              >
                Grandes silhouettes, photos nettes, détails visibles.
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.85, mb: 4 }}>
                Chaque annonce est pensée comme une page éditoriale : matière, tombé, style, pour décider
                en confiance.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                onClick={() => navigate('/search')}
              >
                Louer une robe
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Étapes */}
      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 16 } }}>
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, mb: 2 }}
        >
          Comment ça marche
        </Typography>
        <Typography
          variant="body2"
          align="center"
          color="text.secondary"
          sx={{ mb: 10, maxWidth: 400, mx: 'auto', letterSpacing: '0.04em' }}
        >
          Quatre gestes. Du coup de cœur au bal.
        </Typography>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {[
            { icon: <HowToRegIcon sx={{ fontSize: 32 }} />, title: 'Inscrivez-vous', text: 'Profil en quelques minutes, vérifications simples.' },
            { icon: <SearchIcon sx={{ fontSize: 32 }} />, title: 'Choisissez', text: 'Filtrez par date, taille, style et lieu.' },
            { icon: <ChatIcon sx={{ fontSize: 32 }} />, title: 'Échangez', text: 'Messages directs avec le propriétaire.' },
            { icon: <FavoriteIcon sx={{ fontSize: 32 }} />, title: 'Brillez', text: 'Profitez de votre soirée, puis partagez.' },
          ].map((step, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Box
                sx={{
                  textAlign: 'center',
                  px: 2,
                  py: 1,
                  transition: 'transform 0.35s ease',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 2.5,
                    borderRadius: '50%',
                    border: `1px solid ${alpha('#A8946C', 0.45)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    backgroundColor: alpha(theme.palette.background.default, 0.9),
                  }}
                >
                  {step.icon}
                </Box>
                <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.05rem', fontWeight: 600 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                  {step.text}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA doubles cartes premium */}
      <Box sx={{ backgroundColor: alpha(theme.palette.secondary.light, 0.35), py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  minHeight: { xs: 380, sm: 340 },
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
                  bgcolor: '#fff',
                  transition: 'box-shadow 0.45s ease, transform 0.45s ease',
                  '&:hover': {
                    boxShadow: `0 28px 64px ${alpha(theme.palette.text.primary, 0.07)}`,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', sm: '46%' },
                    minHeight: { xs: 220, sm: 'auto' },
                    backgroundImage: 'url("/images/welcome-dress.jpg")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top',
                  }}
                />
                <Box sx={{ p: { xs: 4, sm: 5 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="overline" sx={{ letterSpacing: '0.28em', color: 'text.secondary', mb: 1.5 }}>
                    Locataires
                  </Typography>
                  <Typography variant="h4" sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, mb: 2 }}>
                    Louer une robe
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                    Parcourez le catalogue, sélectionnez vos dates, réservez en quelques clics.
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12, color: 'inherit' }} />}
                    onClick={() => navigate('/search')}
                    sx={{
                      alignSelf: 'flex-start',
                      backgroundColor: theme.palette.primary.main,
                      color: '#FAF8F5',
                      '&:hover': { backgroundColor: theme.palette.primary.dark, color: '#FAF8F5' },
                      '& .MuiButton-endIcon': { color: '#FAF8F5' },
                    }}
                  >
                    Louer une robe
                  </Button>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  minHeight: { xs: 380, sm: 340 },
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
                  bgcolor: '#fff',
                  transition: 'box-shadow 0.45s ease, transform 0.45s ease',
                  '&:hover': {
                    boxShadow: `0 28px 64px ${alpha(theme.palette.text.primary, 0.07)}`,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', sm: '46%' },
                    minHeight: { xs: 220, sm: 'auto' },
                    background: `linear-gradient(160deg, ${alpha(theme.palette.secondary.main, 0.5)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
                    backgroundImage: 'url("/images/welcome-dress.jpg")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundBlendMode: 'overlay',
                  }}
                />
                <Box sx={{ p: { xs: 4, sm: 5 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="overline" sx={{ letterSpacing: '0.28em', color: 'text.secondary', mb: 1.5 }}>
                    Propriétaires
                  </Typography>
                  <Typography variant="h4" sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, mb: 2 }}>
                    Déposer ma robe
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                    Publiez vos pièces, fixez vos disponibilités, encaissez en toute sécurité.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                    onClick={() => navigate('/add-dress')}
                    sx={{
                      alignSelf: 'flex-start',
                      borderWidth: 2,
                      backgroundColor: alpha('#ffffff', 0.98),
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      color: theme.palette.primary.main,
                      WebkitTextFillColor: theme.palette.primary.main,
                      backgroundImage: 'none',
                      '& .MuiButton-endIcon': { color: 'currentColor' },
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        backgroundImage: 'none',
                        borderColor: theme.palette.primary.main,
                        color: '#ffffff',
                        WebkitTextFillColor: '#ffffff',
                        '& .MuiButton-endIcon': { color: '#ffffff' },
                      },
                    }}
                  >
                    Déposer ma robe
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 8 }}
          >
            <Button variant="text" onClick={() => navigate('/login')} sx={{ letterSpacing: '0.12em', fontSize: '0.75rem' }}>
              Connexion
            </Button>
            <Typography variant="caption" color="text.disabled">
              ·
            </Typography>
            <Button variant="text" onClick={() => navigate('/register')} sx={{ letterSpacing: '0.12em', fontSize: '0.75rem' }}>
              Inscription
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default HomeScreen;
